"use client";

import { ProspectActionKind } from "@prisma/client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { prospectActionKindLabel } from "@/app/(components)/specifics/tools/ProspectSearch/components/ProspectActions/ProspectActions.logic";
import { useFetchClientProspects } from "@/app/hooks/fetchs/prospect-search/useFetchClientProspects";
import { useFetchProspectActionTypes } from "@/app/hooks/fetchs/prospect-search/useFetchProspectActionTypes";
import { ModalStore } from "@/app/stores/ModalStore";
import { useT } from "@/app/translations/useT";
import { PageParams } from "@/app/types/common/DefaultProps";
import { PaginationData } from "@/app/types/common/PaginationData";
import { ProspectActionTypeItem } from "@/app/types/prospect/ProspectAction";
import {
  PROSPECT_ACTION_FILTER_ALL,
  ProspectActionFilter,
  ProspectActionFilterMode,
} from "@/app/types/prospect/ProspectActionFilter";
import { ProspectListTab } from "@/app/types/prospect/ProspectListTab";
import { ProspectsSectionState } from "@/app/types/prospect/ProspectsSectionState";
import { UProspectFilter } from "@/app/utils/UProspectFilter";
import { Globals } from "@/app/vars/Globals";
import { QueryKey } from "@/app/vars/QueryKeys";

type Options = {
  params: PageParams;
  avatarId?: number;
  projectId?: number;
  enabled: boolean;
  isRefreshing?: boolean;
};

export function useProspectsSection(options: Options): ProspectsSectionState {
  const T = useT();
  const { params, avatarId, projectId, enabled, isRefreshing = false } = options;

  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [tab, setTab] = useState(UProspectFilter.readStoredTab);
  const [actionFilter, setActionFilter] = useState(UProspectFilter.readStoredActionFilter);

  const { data: actionTypes } = useFetchProspectActionTypes({
    projectId,
    enabled: enabled && !!projectId,
  });

  const {
    data: clientProspects,
    isLoading,
    isPlaceholderData: isStale,
  } = useFetchClientProspects({
    avatarId,
    page,
    tab,
    actionFilter,
    enabled: enabled && !!avatarId,
    isRefreshing,
    refetchOnWindowFocus: false,
  });

  const resetPage = useCallback(() => setPage(1), []);

  const changeTab = useCallback((next: ProspectListTab) => {
    setTab(next);
    setPage(1);
    UProspectFilter.storeTab(next);
  }, []);

  const changeActionFilter = useCallback((next: ProspectActionFilter) => {
    setActionFilter(next);
    setPage(1);
    UProspectFilter.storeActionFilter(next);
  }, []);

  useEffect(() => {
    if (actionFilter.mode !== ProspectActionFilterMode.TYPE || !actionTypes) {
      return;
    }

    if (!actionTypes.some((type) => type.id === actionFilter.typeId)) {
      changeActionFilter(PROSPECT_ACTION_FILTER_ALL);
    }
  }, [actionTypes, actionFilter, changeActionFilter]);

  const total = clientProspects?.totalCount ?? 0;
  const totalPages = Math.ceil(total / Globals.PROSPECTS_PER_PAGE);

  useEffect(() => {
    if (clientProspects && page > 1 && page > totalPages) {
      setPage(Math.max(1, totalPages));
    }
  }, [clientProspects, page, totalPages]);

  const pagination: PaginationData = {
    page,
    limit: Globals.PROSPECTS_PER_PAGE,
    totalPages,
    hasNext: page < totalPages,
    hasPrevious: page > 1,
  };

  const refreshActions = (prospectId: number) => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECT_ACTIONS, prospectId] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.CLIENT_PROSPECTS] });
  };

  const selectAction = (
    prospectId: number,
    kind: ProspectActionKind,
    type?: ProspectActionTypeItem,
  ) => {
    ModalStore.getState().openProspectActionConfirm(
      params,
      prospectId,
      kind,
      type ? type.label : prospectActionKindLabel(kind, T),
      type?.id,
      () => refreshActions(prospectId),
    );
  };

  const createActionType = (prospectId: number) => {
    if (!projectId) {
      return;
    }

    ModalStore.getState().openProspectActionType(params, projectId, (type) => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECT_ACTION_TYPES, projectId] });
      ModalStore.getState().closeModal();
      selectAction(prospectId, ProspectActionKind.CUSTOM, type);
    });
  };

  const isFiltered =
    tab !== ProspectListTab.ALL || actionFilter.mode !== ProspectActionFilterMode.ALL;

  return {
    prospects: clientProspects?.prospects,
    isLoading,
    isStale,
    total,
    hasList: !isLoading && (total > 0 || isFiltered),
    tab,
    actionFilter,
    actionTypes: actionTypes ?? [],
    pagination,
    setPage,
    resetPage,
    changeTab,
    changeActionFilter,
    selectAction,
    createActionType,
  };
}
